import React, { useState, useRef, useEffect } from 'react';
import './ThemePicker.css';

// Theme definitions
export const THEMES = {
  default: {
    id: 'default',
    name: 'Default',
    colors: {
      background: '#FAF7F2',
      ocean: '#a8d4e6',
      land: '#f5f0e8',
      parks: '#d4e6c8',
      buildings: '#e8e0d5',
      roads: '#d4cfc6',
      borders: '#c5bbb0',
      labels: '#5a5a5a',
      romantic: '#E6B3C2',
      gold: '#F2C6A0',
      legendary: '#B8A0C4',
      // UI colors
      shadows: '#8B857A',
      highlights: '#F2C6A0',
    }
  },
  vanillaSky: {
    id: 'vanillaSky',
    name: 'Vanilla Sky',
    colors: {
      // Dreamy, hazy sky-inspired palette
      background: '#E8E4F0',        // Soft lavender-gray background
      ocean: '#B8C5E8',             // Dreamy periwinkle blue (hazy sky/ocean)
      land: '#E8E0D8',              // Warm, muted cream (like sepia-tinted clouds)
      parks: '#C4D4C8',             // Soft sage with blue undertones
      buildings: '#D4C8C0',         // Muted taupe-gray (dreamy, soft)
      roads: '#C8C0B8',             // Soft, desaturated tan
      borders: '#B8B0A8',           // Muted borders
      labels: '#6B6B7A',            // Soft, readable gray with slight purple tint
      romantic: '#D4B8C8',          // Soft dusty lavender-pink
      gold: '#E8D4B8',              // Warm, muted gold (like sunset)
      legendary: '#C8B8E8',         // Dreamy lavender-purple
      // UI colors
      shadows: '#9B8B8A',           // Soft, muted shadow
      highlights: '#E8D4B8',       // Warm highlight matching gold
    }
  },
  dune: {
    id: 'dune',
    name: 'Dune',
    colors: {
      // Arrakis desert planet palette - warm sands, spice purples, amber skies
      background: '#F4E8D8',        // Warm sand-beige background
      ocean: '#D4A878',             // Deep amber/orange (desert "ocean" - spice fields)
      land: '#E8D4B8',              // Golden sand dunes
      parks: '#C8A878',             // Warm desert vegetation (sparse, golden)
      buildings: '#B89878',         // Sandstone/desert architecture
      roads: '#A88868',             // Deep tan roads (desert paths)
      borders: '#987858',            // Darker desert borders
      labels: '#6B5A4A',            // Deep brown text (readable on sand)
      romantic: '#C8A0D8',          // Spice purple (melange color)
      gold: '#D4A868',              // Amber gold (desert sun)
      legendary: '#9B78C8',         // Deep spice purple (rare melange)
      // UI colors
      shadows: '#8B6B4A',           // Deep desert shadow
      highlights: '#E8B868',        // Bright amber highlight (desert sun)
    }
  },
  darkNight: {
    id: 'darkNight',
    name: 'Dark Night',
    colors: {
      // Deep night palette - dark backgrounds, muted colors, subtle highlights
      background: '#1A1A2E',        // Deep navy-black background
      ocean: '#2A3A5A',             // Dark blue-gray (night ocean)
      land: '#2A2A3E',              // Dark gray-blue (night landscape)
      parks: '#2A3A2A',             // Dark forest green (night vegetation)
      buildings: '#3A3A4E',         // Dark gray buildings
      roads: '#4A4A5A',             // Muted gray roads
      borders: '#5A5A6A',           // Lighter gray borders
      labels: '#B8B8C8',            // Light gray text (readable on dark)
      romantic: '#C8A0D8',          // Soft purple-pink (night accent)
      gold: '#D4B868',              // Muted gold (moonlight)
      legendary: '#9B78C8',         // Deep purple (night sky accent)
      // UI colors
      shadows: '#0A0A1A',           // Deep black shadow
      highlights: '#E8D4B8',       // Soft moonlight highlight
    }
  },
  christmas: {
    id: 'christmas',
    name: 'Christmas',
    colors: {
      // Festive Christmas palette - reds, greens, whites, golds
      background: '#F0F8F0',        // Soft winter white background
      ocean: '#B8D4E8',             // Light blue (winter sky/ice)
      land: '#F5F5F0',              // Snow white land
      parks: '#4A8A4A',             // Deep Christmas green (trees)
      buildings: '#E8E8E0',         // Light gray buildings (snow-covered)
      roads: '#D0D0C8',             // Gray roads (snow-dusted)
      borders: '#A0A0A0',           // Gray borders
      labels: '#3A3A3A',            // Dark text (readable on light)
      romantic: '#D84A5A',          // Festive red (ornaments)
      gold: '#D4A868',              // Warm gold (lights)
      legendary: '#C84A4A',         // Deep red (berries)
      // UI colors
      shadows: '#8B8B8B',           // Gray shadow
      highlights: '#FFD700',        // Bright gold highlight (star)
    }
  }
};

export default function ThemePicker({ currentTheme, onThemeChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleThemeSelect = (themeId) => {
    localStorage.setItem('mapTheme', themeId);
    setIsOpen(false);
    onThemeChange?.(THEMES[themeId]);
  };

  const selectedThemeId = currentTheme?.id || 'default';

  return (
    <div className="theme-picker" ref={dropdownRef}>
      <button
        className="theme-picker-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select map theme"
        aria-expanded={isOpen}
      >
        <span className="theme-picker-icon">🎨</span>
        <span className="theme-picker-label">{currentTheme?.name || 'Default'}</span>
        <span className={`theme-picker-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="theme-picker-dropdown">
          {Object.values(THEMES).map((theme) => (
            <button
              key={theme.id}
              className={`theme-picker-option ${selectedThemeId === theme.id ? 'selected' : ''}`}
              onClick={() => handleThemeSelect(theme.id)}
            >
              <span className="theme-picker-option-name">{theme.name}</span>
              {selectedThemeId === theme.id && (
                <span className="theme-picker-check">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

